import type { UseQueryResult } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SelectParams } from '@/types/api/select';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
}

export interface AsyncSelectProps<T> {
  useQuery: (params?: SelectParams) => UseQueryResult<T[], Error>;
  preload?: boolean;
  filterFn?: (option: T, query: string) => boolean;
  renderOption?: (option: T) => React.ReactNode;
  getOptionValue: (option: T) => string;
  getDisplayValue: (option: T) => React.ReactNode;
  notFound?: React.ReactNode;
  loadingSkeleton?: React.ReactNode;
  value?: string | number;
  onChange: (value: string | number, option: T | null) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  triggerClassName?: string;
  /** Ícone fixo à esquerda do valor selecionado (dentro do próprio trigger). */
  leadingIcon?: React.ReactNode;
  noResultsMessage?: string;
  clearable?: boolean;
  keepData?: boolean;
  align?: 'start' | 'center' | 'end';
  all?: boolean;
  /** Mostra o rótulo em caixa alta dentro do trigger, no padrão dos outros campos do admin. */
  showLabel?: boolean;
  required?: boolean;
  invalid?: boolean;
  /**
   * Atalho para cadastrar na hora: com algo digitado na busca, aparece "Criar …" no fim da
   * lista. Deve devolver a opção criada, que já fica selecionada.
   */
  onCreate?: (search: string) => Promise<T | null | undefined>;
  /** Texto do atalho de criar; recebe o termo digitado. */
  createLabel?: (search: string) => React.ReactNode;
}

export function AsyncSelect<T>({
  useQuery,
  preload,
  filterFn,
  renderOption,
  getOptionValue,
  getDisplayValue,
  notFound = <div>Nenhum resultado encontrado.</div>,
  loadingSkeleton,
  label,
  placeholder = 'Selecione...',
  value,
  onChange,
  disabled = false,
  fullWidth = true,
  className,
  triggerClassName,
  leadingIcon,
  noResultsMessage,
  clearable = true,
  keepData = false,
  align = 'start',
  all = false,
  showLabel = true,
  required,
  invalid,
  onCreate,
  createLabel = (search) => `Criar "${search}"`,
}: AsyncSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);
  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const shouldFetch = preload || open || keepData;
  const queryParams = shouldFetch
    ? {
        search: debouncedSearchTerm,
      }
    : undefined;

  const { data: fetchedData = [], isLoading, isError, error } = useQuery(queryParams);

  const selectedOption = useMemo(() => {
    if (!value || !fetchedData.length) return null;
    const result =
      fetchedData.find((option) => {
        const optValue = getOptionValue(option);
        return optValue == value;
      }) || null;

    return result;
  }, [value, fetchedData, getOptionValue]);

  const options = useMemo(() => {
    if (!fetchedData.length) return [];

    if (preload && filterFn && debouncedSearchTerm) {
      return fetchedData.filter((option) => filterFn(option, debouncedSearchTerm));
    }

    return fetchedData;
  }, [fetchedData, preload, filterFn, debouncedSearchTerm]);

  const handleSelect = useCallback(
    (currentValue: string) => {
      const newValue = clearable && currentValue === value ? '' : currentValue;

      const option =
        fetchedData.find((option) => {
          const optValue = getOptionValue(option);
          return optValue == newValue;
        }) || null;

      onChange(newValue, option);
      setOpen(false);
      setSearchTerm('');
    },
    [value, onChange, clearable, fetchedData, getOptionValue],
  );

  const createTerm = searchTerm.trim();
  const canCreate =
    Boolean(onCreate && createTerm) &&
    !options.some(
      (option) =>
        String(getDisplayValue(option)).toLowerCase() === createTerm.toLowerCase(),
    );

  const handleCreate = async () => {
    if (!onCreate || !createTerm || creating) return;
    setCreating(true);
    try {
      const created = await onCreate(createTerm);
      if (!created) return;
      onChange(getOptionValue(created), created);
      setOpen(false);
      setSearchTerm('');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current && !isLoading) {
      inputRef.current.focus();
    }
  }, [open, isLoading]);

  useEffect(() => {
    if (open && triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="ghost"
          size="lg"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'group relative flex h-[66px] w-full flex-col items-start justify-center gap-1.5 rounded-[14px] border border-border bg-field px-[18px] py-[13px] text-left font-normal',
            // O <Button/> embrulha `children` em dois <span> internos (ver button.tsx)
            // que não esticam sozinhos dentro do botão inline-flex — sem isso o chevron
            // fica colado no texto em vez de ir pra ponta direita do campo.
            '[&>span]:w-full [&>span>span]:w-full',
            'transition-[background-color,border-color] duration-150 hover:bg-field',
            'data-[state=open]:border-olive-line data-[state=open]:bg-accent',
            invalid && 'border-destructive',
            disabled && 'opacity-50 cursor-not-allowed',
            fullWidth && 'w-full',
            triggerClassName,
          )}
          disabled={disabled}
        >
          <span className="relative flex w-full min-w-0 flex-col items-start gap-1.5">
            <span className="pointer-events-none absolute inset-y-[-13px] left-[-18px] w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-data-[state=open]:opacity-100" />

            {showLabel && (
              <span className="col-label text-muted-foreground">
                {label} {required && <span className="text-sand">*</span>}
              </span>
            )}

            <span
              className={cn('flex w-full items-center gap-2.5', leadingIcon && 'pl-0')}
            >
              {leadingIcon && (
                <span className="shrink-0 text-olive-icon">{leadingIcon}</span>
              )}
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-base font-semibold text-white',
                  !selectedOption && value !== 'all' && 'font-medium text-faint',
                )}
              >
                {selectedOption
                  ? getDisplayValue(selectedOption)
                  : value === 'all'
                    ? 'Todos'
                    : placeholder}
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-faint" />
            </span>
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', className)}
        style={{ width: popoverWidth }}
        align={align}
      >
        <Command className="w-full">
          <div className="relative flex items-center border-b px-3 w-full">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <Input
              ref={inputRef}
              placeholder={`Pesquisar ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canCreate && options.length === 0) {
                  e.preventDefault();
                  handleCreate();
                }
              }}
              className="h-10 border-0 bg-transparent pl-0 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
              disabled={isLoading}
            />
            {isLoading && options.length > 0 && (
              <div className="ml-2 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </div>
          <CommandList>
            {isError && (
              <div className="p-4 text-destructive text-center">
                {error?.message || 'Failed to fetch options'}
              </div>
            )}
            {isLoading &&
              options.length === 0 &&
              (loadingSkeleton || <DefaultLoadingSkeleton />)}
            {!isLoading &&
              !isError &&
              options.length === 0 &&
              !canCreate &&
              (notFound || (
                <CommandEmpty>
                  {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                </CommandEmpty>
              ))}
            <CommandGroup className="w-full">
              {all && (
                <CommandItem
                  key={'all'}
                  value={'all'}
                  onSelect={handleSelect}
                  disabled={disabled}
                  className="flex w-full items-center justify-between"
                >
                  <div className="flex-1">Todos</div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0',
                      value === 'all' ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={getOptionValue(option)}
                  value={getOptionValue(option)}
                  onSelect={handleSelect}
                  disabled={disabled}
                  className="flex w-full items-center justify-between"
                >
                  <div className="flex-1">
                    {renderOption ? renderOption(option) : getDisplayValue(option)}
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0',
                      value === getOptionValue(option) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            {canCreate && (
              <CommandGroup className="w-full border-t">
                <CommandItem
                  value={`__create__${createTerm}`}
                  onSelect={handleCreate}
                  disabled={disabled || creating}
                  className="flex w-full items-center gap-2 font-semibold text-primary"
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 shrink-0" />
                  )}
                  <span className="truncate">{createLabel(createTerm)}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DefaultLoadingSkeleton() {
  return (
    <CommandGroup>
      {[1, 2, 3].map((i) => (
        <CommandItem key={i} disabled>
          <div className="flex items-center gap-2 w-full">
            <div className="h-6 w-6 rounded-full animate-pulse bg-muted" />
            <div className="flex flex-col flex-1 gap-1">
              <div className="h-4 w-24 animate-pulse bg-muted rounded" />
              <div className="h-3 w-16 animate-pulse bg-muted rounded" />
            </div>
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}
